import {
  ClipboardCheck, Pill, FlaskConical, HeartPulse, Zap, Stethoscope, HeartPlus, Workflow, ClipboardPen, CalendarDays,
} from 'lucide-react';
import { benTaubOr, BB_DOC, ext } from './benTaubOr';

/*
 * Ben Taub PACT (Pre-Anesthesia Consultation and Testing) rotation. Built from the
 * Blackboard PACT Orientation & Workflow Guide: orientation guide (rev 06/2026),
 * medication reconciliation guide (rev 08/2025), labs and testing guidance (rev 11/2025),
 * risk calculators, GLP-1 fact sheet, special scenarios, ERAS guide and cardiac testing
 * algorithm. See docs/blackboard-ben-taub-subspecialties.md.
 * Contacts, Locations and Documents extend the General OR rotation.
 */

const PACT = BB_DOC('_255039_1');
const meds = (rows) => ({ type: 'table', cols: ['Medication', 'Day of surgery'], rows });

export const benTaubPact = {
  accent: benTaubOr.accent,
  icon: ClipboardCheck,
  tagline: 'Preop clinic: medications, testing, clearance',
  notesKey: 'notes:ben-taub:pact',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'meds', title: 'Medication Guide', subtitle: 'What to hold and continue, by class', icon: Pill, action: 'guide:meds', featured: true },
      { id: 'labs', title: 'Labs & Testing', subtitle: 'When each test is worth ordering', icon: FlaskConical, action: 'guide:labs' },
      { id: 'cardiac', title: 'Cardiac Risk', subtitle: 'Algorithm, RCRI, calculators', icon: HeartPulse, action: 'guide:cardiac' },
      { id: 'cied', title: 'Pacemakers & ICDs', subtitle: 'Magnet, reprogramming, what to document', icon: Zap, action: 'guide:cied' },
      { id: 'scenarios', title: 'Special Scenarios', subtitle: 'Stents, OSA, Jehovah’s Witness, obstetrics', icon: Stethoscope, action: 'guide:scenarios' },
      { id: 'eras', title: 'ERAS in Clinic', subtitle: 'Who qualifies, consents, Ensure, orders', icon: HeartPlus, action: 'guide:eras' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'workflow', title: 'Clinic Workflow', subtitle: 'In person, telephone, orders, not cleared', icon: Workflow, action: 'guide:workflow' },
      { id: 'epic', title: 'Epic', subtitle: 'Schedule, dot phrases, preop note, AVS', icon: ClipboardPen, action: 'guide:epic' },
      { id: 'day', title: 'Your Day', subtitle: 'Hours, census, team, interpreters', icon: CalendarDays, action: 'guide:day' },
    ] },
  ],

  contacts: {
    storageKey: 'ben-taub-pact-favorites',
    groups: [
      { id: 'pact', title: 'PACT team', open: true, items: [
        { label: 'Vu Nguyen, MD, PACT director', phone: '7035975483', note: 'Questions, breaks, issues' },
        { label: 'Shuang Baker, NP', phone: '7133067402' },
        { label: 'Wen Sun, NP', phone: '7139248823' },
        { label: 'Cathy Nuno, NP', phone: '8328301852' },
        { label: 'Colleen Roberts, NP', phone: '9792040508' },
        { label: 'LaShonda Rhodes, NP', phone: '4045792747' },
        { label: 'Karla Maldonado, NP', phone: '8322746353' },
      ] },
      { id: 'clinic', title: 'Clinic', open: true, items: [
        { label: 'Interpreter line', phone: ext('7300'), note: 'Dual-headset phone in each room. Can dial out to telephone patients' },
        { label: 'CCU fellow', phone: ext('2010'), note: 'Device representative for pacemaker or ICD reprogramming' },
      ] },
      ...benTaubOr.contacts.groups,
    ],
  },

  locations: {
    ...benTaubOr.locations,
    groups: [
      { id: 'pact', title: 'PACT clinic', items: [
        { name: 'PACT clinic', floor: 'Ben Taub Tower, 4th floor', where: 'Next to the OB/GYN clinic', note: 'From the garage: into the Tower, through the door behind the front desk, Orange elevators to 4, badge the door on your left, turn right to the end of the hall' },
        { name: 'Resident workroom', floor: 'PACT clinic', note: 'Nurses will show you. NPs are next door. The attending uses the first patient room' },
      ] },
      ...benTaubOr.locations.groups,
    ],
  },

  documents: {
    storageKey: 'ben-taub-pact-pinned-docs',
    groups: [
      { id: 'pact', title: 'PACT', items: [
        { title: 'PACT orientation and guides', subtitle: 'Orientation, medication guide, testing guidance, fact sheets. Blackboard', url: PACT },
      ] },
      { id: 'calculators', title: 'Calculators', items: [
        { title: 'ACS NSQIP surgical risk calculator', url: 'https://riskcalculator.facs.org/RiskCalculator/' },
        { title: 'Revised Cardiac Risk Index', subtitle: 'MDCalc', url: 'https://www.mdcalc.com/calc/1739/revised-cardiac-risk-index-pre-operative-risk' },
        { title: 'STOP-BANG', subtitle: 'MDCalc', url: 'https://www.mdcalc.com/calc/3901/stop-bang-score-obstructive-sleep-apnea' },
        { title: 'Caprini VTE risk', subtitle: 'MDCalc', url: 'https://www.mdcalc.com/calc/4031/caprini-score-venous-thromboembolism-2005' },
      ] },
      ...benTaubOr.documents.groups,
    ],
  },

  guides: {
    meds: {
      title: 'Medication Guide',
      segments: [
        { id: 'basics', label: 'Basics', sections: [
          { title: 'Every patient', blocks: [{ type: 'bullets', items: [
            'Go through the outpatient list: compliance, dose actually taken, last dose. Insulin doses often differ from the chart',
            'Ask about herbals, over-the-counter medications and substance use',
            'Say which medications to hold and when. Circle them on the printout',
            'Anything taken the morning of surgery goes with a sip of water, under 30 mL',
            'Mark as Reviewed under Medication Reconciliation',
            'Instructions in the AVS with .PACTENGMED or .PACTSPAMED at the top, and in the preop note',
          ] }] },
          { title: 'Resuming', blocks: [{ type: 'bullets', items: [
            'Most medications restart once the patient tolerates oral intake',
            'Anything that affects clotting restarts on the surgical team’s call',
          ] }, { type: 'note', text: 'No consensus guidelines exist. Individualise with the PACT attending.' }] },
        ] },
        { id: 'cv', label: 'Cardiac', sections: [
          { title: 'Cardiovascular', blocks: [meds([
            ['Beta blockers', 'Continue. IV if the dose was missed and the patient is NPO. Do not start a new one'],
            ['Clonidine, methyldopa', 'Continue. Transdermal clonidine if no oral intake for over 12 h after surgery, started 3 days before'],
            ['Calcium channel blockers', 'Continue'],
            ['ACE inhibitors, ARBs', 'Hold, unless heart failure or very poorly controlled hypertension. Discuss with the attending'],
            ['Hydrochlorothiazide', 'Continue'],
            ['Loop diuretics', 'Hold if for controlled hypertension or euvolemic heart failure. Continue if poorly controlled. Check potassium'],
            ['Digoxin', 'Continue. No level needed'],
            ['Statins', 'Continue'],
            ['Fibrates, ezetimibe, bile acid resins', 'Stop the day before or the morning of surgery'],
            ['Antiplatelets, anticoagulants', 'Per the surgical team and attending'],
          ])] },
        ] },
        { id: 'resp', label: 'Lung & GI', sections: [
          { title: 'Pulmonary', blocks: [meds([
            ['Inhaled bronchodilators', 'Continue. Give the morning dose if missed: inhaler, nebuliser or through the circuit'],
            ['Inhaled and oral steroids', 'Continue. Stress dosing under Endocrine'],
            ['Montelukast, zafirlukast', 'Continue'],
            ['Theophylline', 'Stop the evening before'],
          ])] },
          { title: 'Gastrointestinal', blocks: [meds([
            ['H2 blockers, PPIs', 'Continue'],
          ])] },
        ] },
        { id: 'dm', label: 'Diabetes', sections: [
          { title: 'Insulin', blocks: [meds([
            ['Basal once daily, basal only', '80% of the usual dose'],
            ['Basal twice daily, basal only', '80% of the evening and morning doses'],
            ['Basal plus prandial', 'Hold all prandial insulin. Morning basal: half the usual total morning dose, basal plus prandial, as basal. Evening basal: 80%, or the full dose if morning glucose runs high. Twice-daily basal: evening 80%, morning half the total as basal'],
            ['Pump', 'Continue the basal rate if the pump can stay on. Otherwise basal insulin per the pump settings 2–3 h before stopping'],
          ])] },
          { title: 'Oral and injectable agents', blocks: [meds([
            ['Metformin', 'Hold'],
            ['Sulfonylureas, meglitinides', 'Hold'],
            ['SGLT2 inhibitors', 'Hold 3–4 days before. Euglycemic ketoacidosis'],
            ['GLP-1 agonists, GI procedures', 'Continue. Ask about nausea, vomiting, abdominal pain, constipation: if present, clear liquids for 24 h before, EGD included. No need to call GI'],
            ['GLP-1 agonists, other surgery', 'Weekly dosing: hold 7 days. Daily: hold 1 day. Consider endocrine for bridging'],
            ['DPP-4 inhibitors', 'Hold'],
            ['Thiazolidinediones', 'Hold'],
          ])] },
        ] },
        { id: 'endo', label: 'Endocrine', sections: [
          { title: 'Thyroid', blocks: [meds([
            ['Levothyroxine', 'Continue. Parenteral only if NPO 5–7 days, at 80% of the oral dose. Subclinical hypothyroidism does not delay surgery'],
            ['Methimazole, PTU', 'Continue. Postpone elective surgery for new overt hyperthyroidism. Consider a beta blocker'],
          ])] },
          { title: 'Steroids', blocks: [meds([
            ['Prednisone under 5 mg/day, under 10 mg every other day, or any dose under 3 weeks', 'Usual dose. No stress dose, no labs'],
            ['Prednisone over 20 mg/day for over 3 weeks, or Cushingoid', 'Usual dose plus stress-dose steroids'],
            ['Prednisone 5–20 mg/day for over 3 weeks', 'Consider an 8 AM cortisol, off steroids 24 h: under 5 mcg/dL impaired, over 10 fine, 5–10 ACTH stimulation test'],
            ['Inhaled or topical steroids', 'Continue. Consider HPA testing after fluticasone 750 mcg/day (1500 mcg others) for 3 weeks, class 1–3 topical 2 g/day for 3 weeks, or Cushingoid features'],
            ['Joint or spinal injections', 'Consider HPA testing after 3 or more within 3 months'],
          ]), { type: 'note', text: 'Prednisone 5 mg = methylprednisolone 4, dexamethasone 0.5, hydrocortisone 20. Ask about steroids in the past year. Avoid etomidate when suppression is possible.' }] },
          { title: 'Hormones and bone', blocks: [meds([
            ['Oral contraceptives', 'Continue with thromboprophylaxis. High VTE risk by Caprini: may stop 4 weeks before with alternative contraception'],
            ['Postmenopausal hormones', 'Continue'],
            ['Tamoxifen, raloxifene', 'Continue for low and moderate risk. High risk: raloxifene off 3 days, tamoxifen for prevention off 2 weeks, tamoxifen for treatment continues. Discuss with oncology'],
            ['Bisphosphonates', 'Hold'],
          ])] },
        ] },
        { id: 'psych', label: 'Psych & other', sections: [
          { title: 'Psychiatric', blocks: [meds([
            ['Tricyclics', 'Continue. Watch for arrhythmia; consider a 7–14 day taper with cardiac disease. No tramadol or meperidine'],
            ['SSRIs, SNRIs', 'Continue'],
            ['MAOIs', 'Decide with psychiatry. If continued: no tramadol, meperidine, methylene blue or dextromethorphan. No ephedrine; careful with every pressor. Phenelzine prolongs succinylcholine'],
            ['Lithium', 'Continue. Watch fluids and electrolytes. Prolongs neuromuscular blockers'],
            ['Antipsychotics', 'Hold if the baseline QT is long. Otherwise continue with care'],
            ['Benzodiazepines', 'Continue. Parenteral if missed'],
            ['Buspirone', 'Continue. No tramadol or meperidine'],
            ['Stimulants', 'Hold'],
            ['Naltrexone', 'Hold. Plan multimodal analgesia; opioids still work'],
          ])] },
          { title: 'Other', blocks: [meds([
            ['Allopurinol', 'Continue'],
            ['Colchicine', 'Hold'],
            ['Tamsulosin and other alpha-1 blockers', 'Continue. Tell the ophthalmologist: floppy iris'],
            ['Garlic, ginseng', 'Stop 7 days before'],
            ['Ginkgo', 'Stop 36 h before'],
            ['Ephedra, kava', 'Stop 24 h before'],
            ['St John’s wort', 'Stop 5 days before'],
            ['Valerian', 'Taper over weeks'],
          ])] },
        ] },
      ],
      sources: [{ title: 'PACT medication reconciliation guide', href: PACT, detail: 'Revised August 2025. Blackboard, sign-in required' }],
    },

    labs: {
      title: 'Labs & Testing',
      sections: [
        { title: 'Principles', blocks: [{ type: 'bullets', items: [
          'Order for a patient or procedure reason, never as a screen',
          'Nothing for minimally invasive, low-risk procedures',
          'Do not repeat a test under 6 months old if nothing has changed',
          'Age alone is not a reason',
          'Skip anything that will not change management',
        ] }] },
        { title: 'By test', blocks: [{ type: 'table', cols: ['Test', 'Reasonable when'], rows: [
          ['CBC', 'Large expected blood loss. Over 65 and major surgery. Anemia symptoms. Neuraxial planned. Intracranial surgery. Bleeding signs or thrombocytopenic medications'],
          ['BMP', 'Major surgery with diabetes, hypertension, heart failure or kidney disease. Over 50. ACE inhibitor, ARB, diuretic or NSAID. Creatinine over 2 is an RCRI factor'],
          ['LFTs', 'Suspected acute hepatitis, cirrhosis or liver failure only'],
          ['PT, PTT, INR', 'Bleeding history, kidney or liver disease, medications that affect clotting, high-bleeding-risk surgery. History predicts bleeding better than the test'],
          ['Urinalysis', 'Endoscopic urologic procedures with mucosal trauma: screen and treat. Not before other elective surgery'],
          ['Pregnancy test', 'Every woman under 55 without a hysterectomy, ordered for the day of surgery'],
          ['ECG', 'Elevated-risk surgery with coronary, peripheral arterial, cerebrovascular or structural heart disease, or significant arrhythmia. Not for low risk. Act on Mobitz II or higher block, AF with RVR or new AF, long QT'],
          ['Echo', 'New dyspnea of suspected cardiac origin, heart failure on exam, suspected new or worsening ventricular dysfunction, known heart failure with a change. Not when stable'],
          ['Stress test', 'Elevated-risk surgery, poor or unknown functional capacity, and elevated risk on a validated tool. Not with METs over 4 and stable symptoms'],
          ['Chest X-ray', 'Active cardiopulmonary symptoms only'],
        ] }] },
      ],
      sources: [{ title: 'PACT labs and testing guidance', href: PACT, detail: 'Revised November 2025. Blackboard, sign-in required' }],
    },

    cardiac: {
      title: 'Cardiac Risk',
      sections: [
        { title: 'Algorithm', blocks: [{ type: 'steps', items: [
          'Emergency: clinical risk stratification and proceed',
          'Acute coronary syndrome: evaluate and treat first',
          'Low-risk procedure, under 1% combined risk (cataract, endoscopy, breast, superficial skin, dental extraction): no testing',
          'Elevated risk with METs over 4: no testing',
          'METs under 4 or unknown, and the result would change management: pharmacologic stress test. Abnormal result: revascularisation per guideline',
        ] }] },
        { title: 'Revised Cardiac Risk Index', blocks: [{ type: 'bullets', items: [
          'High-risk surgery: intrathoracic, intra-abdominal, suprainguinal vascular',
          'Ischemic heart disease without revascularisation',
          'Heart failure',
          'Cerebrovascular disease',
          'Insulin-treated diabetes',
          'Creatinine over 2.0',
        ] }, { type: 'rows', rows: [
          { label: '0–1 factor', value: '0.4–1.3%' },
          { label: '2 factors', value: '4–7%' },
          { label: '3 or more', value: '9–11%' },
        ] }, { type: 'note', text: 'Major cardiac complications: MI, pulmonary edema, VF, cardiac arrest, complete heart block. Underestimates risk in major vascular surgery.' }] },
        { title: 'Calculators', blocks: [{ type: 'rows', rows: [
          { label: 'NSQIP MICA', value: 'MI or cardiac arrest at 30 days', note: 'Surgery type, ASA, functional dependence, age, creatinine. Better than RCRI' },
          { label: 'ACS surgical risk calculator', value: 'Cardiac and non-cardiac complications, death', note: '21 factors, most comprehensive' },
        ] }, { type: 'note', text: '2024 ACC/AHA: no calculator is preferred. Links under Documents.' }] },
      ],
      sources: [{ title: 'Risk calculators and cardiac testing algorithm', href: PACT, detail: 'Blackboard, sign-in required' }],
    },

    cied: {
      title: 'Pacemakers & ICDs',
      sections: [
        { title: 'Electrocautery above the umbilicus?', blocks: [{ type: 'rows', rows: [
          { label: 'No', value: 'Proceed. Magnet available' },
          { label: 'Pacemaker, dependent', value: 'Reprogram asynchronous (rep preferred), or magnet if it makes the pacemaker asynchronous' },
          { label: 'Pacemaker, not dependent', value: 'Consider reprogramming or a magnet if cautery is within 15 cm. Otherwise magnet available' },
          { label: 'ICD', value: 'Deactivate by reprogramming or magnet. Pads on. If also dependent, reprogram asynchronous' },
        ] }] },
        { title: 'Pearls', blocks: [{ type: 'bullets', items: [
          'Dependent means paced 40% of the time or more',
          'Grounding pad on a lower extremity, current path away from the device. Bipolar when possible',
          'Interrogation within 6 months for an ICD, 12 months for a pacemaker, 3–6 months for CRT',
          'Interrogation report under the Media tab in Epic: mode, battery, dependence',
          'Reprogramming needed: page the CCU fellow for the device representative. Arrange ahead; they are not always on site',
          'ICD off: pads on, and the representative turns it back on in the OR',
          'Asynchronous pacemaker: revert to the usual settings at the end',
        ] }] },
        { title: 'Document', blocks: [{ type: 'steps', items: [
          'Date of last interrogation',
          'Device type, manufacturer, model',
          'Indication',
          'Battery longevity',
          'Leads placed within 3 months',
          'Current program',
          'Pacemaker dependent?',
          'Response to a magnet',
          'Alerts on the device',
          'Last pacing threshold',
          'Perioperative recommendation',
        ] }] },
      ],
      sources: [{ title: 'Perioperative CIED protocol', href: PACT, detail: 'PACT orientation guide, appendix 3. Blackboard, sign-in required' }],
    },

    scenarios: {
      title: 'Special Scenarios',
      sections: [
        { title: 'Coronary stents', items: [
          'Stent type, location and date',
          'Cardiac history, recent angina, heart failure symptoms, echo and stress results',
          'Antiplatelet and anticoagulant regimen. Continue or stop with the surgical team',
        ] },
        { title: 'Pacemaker or ICD', items: [
          'ICD or pacemaker only. Manufacturer, model, year, last interrogation, shocks or malfunction',
          'Interrogation report under the Media tab: mode, battery, dependence',
          'Dependent: device representative confirms the magnet response. No representative: cardiology for day-of-surgery reprogramming',
        ] },
        { title: 'Sleep apnea', items: [
          'STOP-BANG and any sleep study',
          'Severity and CPAP compliance',
          'Bring the CPAP machine on the day of surgery',
        ] },
        { title: 'Jehovah’s Witness', items: [
          'Which products are acceptable: albumin, recombinant factors, cell saver. Coordinate with the surgical team',
          'Optimise anemia; erythropoietin if needed',
        ] },
        { title: 'Obstetric', items: [
          'Prior cesarean indication and anesthetic, current pregnancy, placental location. Previa or accreta: tell L&D faculty right away',
          'Cesarean within 72 h: CBC and type and screen now. Later: type and screen drawn on the day',
          'Crossmatch 2 units on the day if Hgb under 10 or Hct under 30',
          'High risk: 4–6 units RBC, 1 jumbo FFP, 1 jumbo platelets. Discuss with L&D faculty',
        ] },
      ],
      sources: [{ title: 'Special scenarios in PACT', href: PACT, detail: 'Blackboard, sign-in required' }],
    },

    eras: {
      title: 'ERAS in Clinic',
      sections: [
        { title: 'Who is on the pathway', blocks: [{ type: 'table', cols: ['Service', 'Surgeries'], rows: [
          ['Gynecology', 'Open hysterectomy, laparoscopic hysterectomy, tumor debulking'],
          ['Obstetrics', 'Cesarean section'],
          ['Urology', 'Nephrectomy, cystectomy, RPLND'],
          ['Thoracic and CV', 'All'],
          ['Colorectal', 'APR, LAR, pelvic exenteration, laparotomy with colectomy or resection'],
        ] }] },
        { title: 'Every ERAS patient', blocks: [{ type: 'steps', items: [
          'NoteWriter, ROS/Med Hx tab: tap Patient on ERAS protocol',
          'PACT nurses give the written instructions',
          'Ensure: 2 bottles, unless on insulin (oral agents are fine). None for any diabetic cesarean patient',
          'Consent for general, epidural, CSE and regional blocks',
        ] }] },
        { title: 'Cesarean', blocks: [{ type: 'bullets', items: [
          'Consent for spinal, epidural, general, regional, and perinatal complications',
          'Clear liquids until 2 h before. Use judgement with morbid obesity or gastroparesis',
          'Two bottles of Clear Ensure. No Ensure with gestational or other diabetes; they stay on the pathway',
          'No preoperative oral multimodal analgesics',
        ] }] },
        { title: 'Thoracic', blocks: [{ type: 'bullets', items: [
          'Type and screen qualifies for a 30-day extended expiry. Usually ordered by the CV team: check it is in, otherwise order it with the extended-expiry comment from the guide',
        ] }] },
      ],
      sources: [{ title: 'ERAS/ERAC guide', href: PACT, detail: 'Blackboard, sign-in required' }],
    },

    workflow: {
      title: 'Clinic Workflow',
      segments: [
        { id: 'inperson', label: 'In person', sections: [
          { title: 'Visit', blocks: [{ type: 'steps', items: [
            'Patients register downstairs, then nursing rooms them: vitals, medication reconciliation, surgical instructions',
            'Evaluate in the patient’s preferred language. Dual-headset phone in each room. Document the interpreter’s name and ID; .INTERPTEXT in the note',
            'Preop note from the Pre-Evaluation tab: Physical Exam, Anesthesia Plan (.BTPREOP), Note Text. Write a skeleton from the chart first',
            'Reconcile medications and give day-of-surgery instructions. AVS with .PACTENGMED or .PACTSPAMED at the top',
            'Consent everyone for general anesthesia, even if MAC is planned. GI is general TIVA. Patient signs the e-consent first, then the nurse witnesses',
            'Present to the attending, who attests. Airway or medical concerns before the patient leaves, whatever the ASA',
            'Orders, then tell nursing what you ordered and when the visit is done',
            'Sign with name, title, date and HH ID',
          ] }] },
        ] },
        { id: 'tele', label: 'Telephone', sections: [
          { title: 'Calls', blocks: [{ type: 'bullets', items: [
            'Only call "Signed in" or "Present" patients. Call early at most once; never mark a no-show outside the appointment time',
            'Mark the call in Epic at the start: PAT Screening Call tab, Outgoing call. Comment if no answer',
            'Interpreters dial out to the patient. Batch calls that need the same language',
            'The nurse usually calls first and transfers. Check the reconciliation date is today; otherwise do it yourself',
            'No exam: gauge functional capacity and cardiopulmonary status',
            'Explain the plan and the medication instructions. No consent by phone; it is signed on the day',
            'Sign out with the attending, singly or in batches. Extra orders go to the clinic nurse and the patient',
          ] }] },
        ] },
        { id: 'orders', label: 'Orders', sections: [
          { title: 'Every patient', blocks: [{ type: 'bullets', items: [
            'IP ANE PRE-OP HOLDING ORDERS. Not the Pre-Anesthesia Testing Clinic orders. Add it to favourites',
            'Check or uncheck: pregnancy test (women under 55), glucose (diabetes), potassium (ESRD)',
            'ERAS orders when the surgery qualifies',
            'Sign and hold',
          ] }] },
          { title: 'Extra tests', blocks: [{ type: 'bullets', items: [
            'ECG: order and tell the nurse. Done in clinic, read with the attending before discharge',
            'Results land in your in-basket the next day. Follow them up',
            'Antibody-positive screen: tell the primary team; they may crossmatch',
            'Anything that could delay surgery: attending and primary team',
          ] }] },
        ] },
        { id: 'notcleared', label: 'Not cleared', sections: [
          { title: 'Steps', blocks: [{ type: 'steps', items: [
            'Decide with the attending. Order the tests',
            'Medicine or cardiology: IP Consult to General Medicine, then tell the clinic nurse so the visit gets booked',
            'Tell the surgical team by in-basket or secure chat if surgery will move',
            'Call the patient. If surgery is delayed, make sure they know not to come in',
            'Patient has NOT BEEN CLEARED by Anesthesia button on the preop note',
            'Follow the consult and clear with the preop attending. The NPs can help',
          ] }] },
        ] },
      ],
      sources: [{ title: 'PACT orientation guide', href: PACT, detail: 'Revised June 2026. Blackboard, sign-in required' }],
    },

    epic: {
      title: 'Epic',
      sections: [
        { title: 'Your clinic schedule', blocks: [{ type: 'steps', items: [
          'Department: BT ANESTHESIA SERVICES',
          'Clinic schedule tab, your name, Create, name the schedule',
          'Configuration tab, Provider [All Depts]',
          'Add BT ANES PR 1, BT ANES PR 2 and ANESTHESIA, RESIDENT. Accept',
        ] }] },
        { title: 'Reading the schedule', blocks: [{ type: 'rows', rows: [
          { label: 'Colour dot', value: 'Provider', note: 'Residents red or blue. Pink nursing only. White no-show. Sort by it' },
          { label: 'Status', value: 'Signed in, Present: call', note: 'Scheduled: not yet' },
          { label: 'Visit type', value: 'Eval in person, Tele by phone' },
          { label: 'Notes', value: 'Service, date, UTR, LVM' },
        ] }] },
        { title: 'Dot phrases', blocks: [{ type: 'rows', rows: [
          { label: '.BTPREOP', value: 'Anesthesia Plan smartblock', note: 'Shared by Dr Nguyen' },
          { label: '.INTERPTEXT', value: 'Interpreter use', note: 'Required and audited. Auto-populates for non-English preferred language' },
          { label: '.PACTENGMED', value: 'AVS instructions, English' },
          { label: '.PACTSPAMED', value: 'AVS instructions, Spanish' },
        ] }] },
        { title: 'Preop note', blocks: [{ type: 'bullets', items: [
          'Pre-Evaluation on the sidebar: tap the text to open the note. The arrow is read-only',
          'Three tabs: Physical Exam, Anesthesia Plan, Note Text',
          'Problem list opens on the left; tick existing problems to pull them in',
        ] }] },
        { title: 'After-visit summary', blocks: [{ type: 'steps', items: [
          'PAT screen, Patient Instructions tab',
          'Dot phrase at the top, above nursing instructions. F2 through the blanks',
          'Close to save. Copy the same instructions into the preop note',
        ] }] },
      ],
      sources: [{ title: 'PACT orientation guide', href: PACT, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'Hours', blocks: [{ type: 'bullets', items: [
          '07:00 to 16:00, or until the last patient is dismissed by the attending. Wednesdays after Grand Rounds',
          'First patients 07:00–07:30. NPs arrive at 08:00; you see the early patients',
          'No scheduled breaks; take them when the flow allows',
          'Check out with the PACT attending before leaving. Help whoever still has complex patients',
          'Released early: call the CON. You cannot leave the hospital unless they dismiss you. Late or Z: tell the CON and senior so you can relieve someone',
        ] }] },
        { title: 'Census', blocks: [{ type: 'bullets', items: [
          'About 54 patients a day. Cap 60 with five providers, 50 with four, 40 with three, add-ons aside',
          '12–15 in person, the rest by phone. NPs assign patients the day before',
          'GI, ophthalmology, OB, gynecology, IR, orthopedics, urology, ENT, OMFS, vascular, thoracic, general surgery, plastics, pulmonary, cardiology, ECT',
        ] }] },
        { title: 'Team', blocks: [{ type: 'bullets', items: [
          'Attending rotates daily: CON report on QGenda',
          'Six NPs, 08:00–16:00 weekdays. Leave your number with the NPs and nurses on day one',
          'Patient care coordinators for each service are posted on the workroom bulletin board; reach them by in-basket or secure chat',
        ] }] },
        { title: 'Interpreters', blocks: [{ type: 'bullets', items: [
          'Telephone line on the dual-headset phones; in-person Spanish; video tablet for sign language',
          'Document name and ID. .INTERPTEXT in every interpreted note',
        ] }] },
      ],
      sources: [{ title: 'PACT orientation guide', href: PACT, detail: 'Blackboard, sign-in required' }],
    },
  },
};
